import { Injectable } from '@angular/core';
import Shepherd, { type StepOptions, type Tour, type TourOptions } from 'shepherd.js';
import { elementIsHidden, makeButton } from '@shared/modules/shepherd/shepherd.utils';

interface RequiredElement {
  message: string;
  selector: string;
  title: string;
}

@Injectable({
  providedIn: 'root',
})
export class ShepherdService {
  confirmCancel: TourOptions['confirmCancel'] = false;
  confirmCancelMessage?: TourOptions['confirmCancelMessage'];
  defaultStepOptions: StepOptions = {};
  errorTitle?: string;
  exitOnEsc: TourOptions['exitOnEsc'] = true;
  isActive = false;
  keyboardNavigation: TourOptions['keyboardNavigation'] = true;
  messageForUser: string | null = null;
  modal = false;
  requiredElements: RequiredElement[] = [];
  tourName: TourOptions['tourName'] = undefined;
  tourObject: Tour | null = null;

  constructor() {}

  back() {
    this.tourObject?.back();
  }

  cancel() {
    this.tourObject?.cancel();
  }

  complete() {
    this.tourObject?.complete();
  }

  hide() {
    this.tourObject?.hide();
  }

  next() {
    this.tourObject?.next();
  }

  show(id: string | number) {
    this.tourObject?.show(id);
  }

  start() {
    this.isActive = true;
    this.tourObject?.start();
  }

  addSteps(steps: StepOptions[]) {
    this._initialize();
    const tour = this.tourObject;

    // Return nothing if there are no steps or if somehow there is no tour.
    if (!tour || !steps || !Array.isArray(steps) || steps.length === 0) {
      return;
    }

    if (!this.requiredElementsPresent()) {
      tour.addStep({
        buttons: [
          {
            text: 'Exit',
            action: tour.cancel,
          },
        ],
        id: 'error',
        title: this.errorTitle,
        text: [this.messageForUser as string],
      });
      return;
    }

    steps.forEach((step) => {
      if (step.buttons) {
        step.buttons = step.buttons.map(makeButton.bind(this), this);
      }

      tour.addStep(step);
    });
  }

  private requiredElementsPresent() {
    let allElementsPresent = true;

    /* istanbul ignore next: also can't test this due to things attached to root blowing up tests */
    this.requiredElements.forEach((element) => {
      const selectedElement = document.querySelector(element.selector);

      if (allElementsPresent && (!selectedElement || elementIsHidden(selectedElement as HTMLElement))) {
        allElementsPresent = false;
        this.errorTitle = element.title;
        this.messageForUser = element.message;
      }
    });

    return allElementsPresent;
  }

  private _initialize() {
    const tourObject = new Shepherd.Tour({
      confirmCancel: this.confirmCancel,
      confirmCancelMessage: this.confirmCancelMessage,
      defaultStepOptions: this.defaultStepOptions,
      keyboardNavigation: this.keyboardNavigation,
      tourName: this.tourName,
      useModalOverlay: this.modal,
      exitOnEsc: this.exitOnEsc,
    });

    tourObject.on('complete', () => (this.isActive = false));
    tourObject.on('cancel', () => (this.isActive = false));

    this.tourObject = tourObject;
  }
}
